/**
 * The floor.
 *
 * WHY THIS IS A GRID AND NOT A TABLE OF ROWS
 * ------------------------------------------
 * It is read at a glance, from a few feet away, by somebody carrying plates.
 * The question is "which of these is free" and "how long has 6 been sitting
 * there" — both answered by shape and colour faster than by reading a column.
 *
 * WHAT MAKES A TABLE OCCUPIED
 * ---------------------------
 * An unpaid bill. Not the order's status: in this system a dine-in order sits
 * at "confirmed" for ever because nothing moves it on, so a floor built on
 * status would show every table occupied since the shop opened. Settling up
 * is what ends a meal.
 *
 * WHY THE TIME MATTERS MORE THAN THE MONEY
 * ----------------------------------------
 * A table that has been sitting ninety minutes is either a long lunch or a
 * bill nobody has taken over. Both want a look, and neither shows up in the
 * total.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatPKR } from '@foodrun/shared';

import { sittingFor, isLingering } from './sitting.js';

import { api } from '../../lib/api.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { TableForm } from './TableForm.jsx';
import { useAuth } from '../../stores/auth.js';

const AREAS = {
  main: 'Inside',
  outdoor: 'Outside',
  terrace: 'Terrace',
  private: 'Private room',
  counter: 'Counter',
};

export default function FloorPage() {
  /**
   * Laying the floor out, which nothing could do before.
   *
   * `null` is closed; `{}` is a new table; a table object is that one being
   * changed. Gated on the same permission the branch checks, so nobody is
   * shown a door they cannot walk through.
   */
  const [editing, setEditing] = useState(null);
  const canArrange = useAuth((s) => s.can)('pos.create.pos');

  const floor = useQuery({
    queryKey: ['pos', 'floor'],
    queryFn: () => api.get('/pos/tables'),
    // Read continuously through service, so it keeps itself current without
    // anybody thinking to reload.
    refetchInterval: 20_000,
  });

  if (floor.isLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner label="Looking at the floor" />
      </div>
    );
  }

  if (floor.isError) {
    return (
      <p className="py-16 text-center text-sm text-ink-2">
        Could not see the floor. Check the branch computer is running.
      </p>
    );
  }

  const tables = floor.data?.floor ?? [];
  const summary = floor.data?.summary;

  /**
   * A shop that has not drawn its floor plan yet.
   *
   * THE DEAD END THIS FIXES
   * -----------------------
   * This returned early, before the "Add a table" button further down was
   * ever rendered — so the button only existed once a table already did.
   * With none, the page said to add them under Branches, which has no table
   * form and never has.
   *
   * So the first table could not be added from the interface by any route.
   * Found on a live server by an owner looking for the button.
   *
   * The empty state now carries the same control as the populated one,
   * which is the only arrangement that makes sense: this is precisely the
   * moment somebody wants it.
   */
  if (tables.length === 0) {
    return (
      <div className="mx-auto max-w-4xl p-4">
        <h1 className="mb-2 font-semibold text-xl text-ink">Floor</h1>
        <p className="mb-4 text-sm text-ink-2">
          {canArrange
            ? 'No tables yet. Add the ones in your dining room and they will appear here.'
            : 'No tables have been set up yet. A manager can add them.'}
        </p>

        {canArrange && (
          <Button variant="secondary" onClick={() => setEditing({})}>
            Add a table
          </Button>
        )}

        {editing && (
          <TableForm
            table={editing.id ? editing : null}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              floor.refetch();
            }}
          />
        )}
      </div>
    );
  }

  const byArea = tables.reduce((groups, table) => {
    (groups[table.area] ??= []).push(table);
    return groups;
  }, {});

  return (
    <div className="mx-auto max-w-5xl p-4">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold text-xl text-ink">Floor</h1>
          <p className="mt-0.5 text-sm text-ink-2">
            {summary.free} of {summary.tables} free
          </p>
        </div>

        <div className="flex items-end gap-3 text-right">
          {canArrange && (
            <Button variant="secondary" onClick={() => setEditing({})}>
              Add a table
            </Button>
          )}

          <div>
          <span className="block text-lg font-semibold tabular-nums text-ink">
            {formatPKR(summary.openValue)}
          </span>
          {/* What is sitting on the floor unpaid — the figure a manager
              glances at, and the one that should not still be there at close. */}
          <span className="block text-xs text-ink-3">on the floor, unpaid</span>
          </div>
        </div>
      </div>

      <div className="space-y-5">
        {Object.entries(byArea).map(([area, group]) => (
          <section key={area}>
            <h2 className="mb-2 text-sm font-medium text-ink-2">{AREAS[area] ?? area}</h2>

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {group.map((table) => (
                <TableCard
                  key={table.id}
                  table={table}
                  onEdit={canArrange ? () => setEditing(table) : null}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      {editing && (
        <TableForm
          table={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            floor.refetch();
          }}
        />
      )}
    </div>
  );
}

function TableCard({ table, onEdit }) {
  const sitting = table.order ? sittingFor(table.order.seatedAt) : null;
  const lingering = table.occupied && isLingering(table.order?.seatedAt);

  return (
    <div
      className={`rounded-xl border p-3 ${
        table.occupied
          ? lingering
            ? 'border-warn bg-warn-bg'
            : 'border-brand-400 bg-surface'
          : 'border-rule bg-surface-3'
      }`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className={`font-medium ${table.occupied ? 'text-ink' : 'text-ink-3'}`}>
          {table.number}
        </span>

        <div className="flex items-center gap-1.5">
          <span className="text-xs text-ink-3">{table.seats} seats</span>
          {onEdit && (
            <button
              onClick={onEdit}
              aria-label={`Edit or delete table ${table.number}`}
              title="Edit or delete"
              className="-mr-1 rounded p-1 text-ink-3 hover:bg-surface-2 hover:text-ink"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {table.occupied ? (
        <>
          <span className="mt-2 block text-sm tabular-nums text-ink">
            {formatPKR(table.order.total)}
          </span>
          <span className={`block text-xs ${lingering ? 'text-warn' : 'text-ink-3'}`}>
            {sitting}
            {table.order.servedBy && ` · ${table.order.servedBy}`}
          </span>
          <span className="mt-1 block truncate text-xs tabular-nums text-ink-3">
            {table.order.number}
          </span>
        </>
      ) : (
        <span className="mt-2 block text-sm text-ink-3">Free</span>
      )}
    </div>
  );
}
